import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { AppRouteErrorFallback } from '@/app/errors/AppRouteErrorFallback';
import { RouteLoading } from '@/app/router/RouteLoading';
import { pageModules } from '@/app/router/pageModules';
import { requireAuth } from '@/app/router/requireAuth';
import { CreateProjectDialog } from '@/features/create-project/ui/CreateProjectDialog';
import { NotFoundPage } from '@/pages/not-found/NotFoundPage';
/** 首页懒加载页面组件，使用大写别名以符合 JSX 组件命名约定。 */
const HomePage = pageModules.home;
/** 编辑器懒加载页面组件，使用大写别名以符合 JSX 组件命名约定。 */
const EditorPage = pageModules.editor;
/** 项目素材创作中心懒加载页面组件，承载当前项目的素材管理与视频创作。 */
const MaterialCreationPage = pageModules.materialCreation;
/** 影游大赏懒加载页面组件，使用大写别名以符合 JSX 组件命名约定。 */
const AwardsPage = pageModules.awards;
/** 影游大赏项目详情懒加载页面组件，承接大赏卡片点击进入。 */
const AwardProjectDetailPage = pageModules.awardDetail;
/** 素材中心懒加载页面组件，管理当前用户生成和上传的素材。 */
const AssetsPage = pageModules.assets;
/** 我的项目懒加载页面组件，使用大写别名以符合 JSX 组件命名约定。 */
const ProjectsPage = pageModules.projects;
/** 回收站懒加载页面组件，使用大写别名以符合 JSX 组件命名约定。 */
const RecyclePage = pageModules.recycle;
/** 项目详情懒加载页面组件，承载横向导航与各功能页签内容。 */
const ProjectDetailPage = pageModules.projectDetail;
/** 项目级素材库独立页面，脱离项目详情页签框架。 */
const ProjectAssetsPage = pageModules.projectAssets;
/** 项目素材回收站页面，承载素材库中已删除素材的恢复与清空。 */
const ProjectAssetsRecyclePage = pageModules.projectAssetsRecycle;
/** 项目详情页签内容分发懒加载组件，按路径段渲染当前页签面板。 */
const ProjectTabContent = pageModules.projectTabContent;
/** 新版编剧视图前端原型，复用章节与剧情树读取数据。 */
const WriterPage = pageModules.writer;
/** 单章剧情树独立深链懒加载组件。 */
const StoryTreePage = pageModules.storyTree;
/** AI 分支选择概率分析页面懒加载组件。 */
const StoryBranchAnalysisPage = pageModules.storyBranchAnalysis;
/** 开始页配置编辑器懒加载页面组件，承载载入页、前置动画与开始页配置。 */
const StartPageEditorPage = pageModules.startPage;
/** 项目全屏播放懒加载页面组件。 */
const ProjectPlayPage = pageModules.play;
/** 项目团队与权限管理懒加载页面组件。 */
const ProjectManagementPage = pageModules.projectManagement;
/** 公开邀请加入项目落地页懒加载组件。 */
const ProjectJoinPage = pageModules.projectJoin;
/** 个人中心懒加载页面组件，聚合当前账号资料与内容列表。 */
const PersonalCenterPage = pageModules.personalCenter;
const PointsCenterPage = pageModules.pointsCenter;
const PointsRulesPage = pageModules.pointsRules;
const PointsPaymentAgreementPage = pageModules.pointsPaymentAgreement;
/** 用户服务条款正文页懒加载组件，登录弹窗协议勾选行新标签页进入。 */
const UserAgreementPage = pageModules.userAgreement;
/** 隐私政策正文页懒加载组件，登录弹窗协议勾选行新标签页进入。 */
const PrivacyPolicyPage = pageModules.privacyPolicy;
/** 新版项目工作区子路由：脉络图为默认页，开始页配置与编剧视图是独立深链页面。 */
const projectDetailTabRoutes = [
    { index: true, element: _jsx(ProjectTabContent, {}) },
    { path: 'writer', element: _jsx(WriterPage, {}) },
    { path: 'start-page', element: _jsx(StartPageEditorPage, {}) },
    { path: 'management', element: _jsx(ProjectManagementPage, {}) },
];
/**
 * 应用路由配置（数据路由形态）。
 *
 * 受保护页面统一挂 `requireAuth` loader：登录校验在渲染前完成，
 * 未登录时直接重定向首页，不再产生「上一页卸载 + 目标页空渲染」的中间帧。
 * 根布局同时提供懒加载 Suspense 边界、初始加载占位与路由级错误降级界面。
 * @returns 可直接交给 createBrowserRouter / createMemoryRouter 的路由表。
 */
export function buildAppRoutes() {
    return [
        {
            // 根布局：不占路径，只负责统一兜住懒加载与路由错误。
            HydrateFallback: RouteLoading,
            errorElement: _jsx(AppRouteErrorFallback, {}),
            element: (_jsxs(_Fragment, { children: [_jsx(Suspense, { fallback: _jsx(RouteLoading, {}), children: _jsx(Outlet, {}) }), _jsx(CreateProjectDialog, {})] })),
            children: [
                // 首页与影游大赏对外公开展示，匿名可访问。
                { path: '/', element: _jsx(HomePage, {}) },
                { path: '/awards', element: _jsx(AwardsPage, {}) },
                { path: '/awards/:projectId', element: _jsx(AwardProjectDetailPage, {}) },
                { path: '/join/:invitationToken', element: _jsx(ProjectJoinPage, {}) },
                // 以下均为登录后可用的工作台页面，统一由 requireAuth loader 守卫。
                { path: '/assets', element: _jsx(AssetsPage, {}), loader: requireAuth },
                { path: '/projects', element: _jsx(ProjectsPage, {}), loader: requireAuth },
                { path: '/personal-center', element: _jsx(PersonalCenterPage, {}), loader: requireAuth },
                { path: '/points', element: _jsx(PointsCenterPage, {}), loader: requireAuth },
                { path: '/points/rules', element: _jsx(PointsRulesPage, {}), loader: requireAuth },
                { path: '/points/payment-agreement', element: _jsx(PointsPaymentAgreementPage, {}) },
                // 协议正文页同样匿名可访问：登录前就要能查阅用户服务条款与隐私政策。
                { path: '/agreement/user-agreement', element: _jsx(UserAgreementPage, {}) },
                { path: '/agreement/privacy-policy', element: _jsx(PrivacyPolicyPage, {}) },
                // 静态路由需先于 /projects/:projectId 注册，避免被动态段吞掉。
                { path: '/projects/recycle', element: _jsx(RecyclePage, {}), loader: requireAuth },
                {
                    path: '/projects/:projectId/chapters/:chapterId/story-tree',
                    element: _jsx(StoryTreePage, {}),
                    loader: requireAuth,
                },
                { path: '/projects/:projectId/play', element: _jsx(ProjectPlayPage, {}), loader: requireAuth },
                {
                    path: '/projects/:projectId/assets/recycle',
                    element: _jsx(ProjectAssetsRecyclePage, {}),
                    loader: requireAuth,
                },
                {
                    path: '/projects/:projectId/assets',
                    element: _jsx(ProjectAssetsPage, {}),
                    loader: requireAuth,
                },
                {
                    path: '/projects/:projectId/material-creation',
                    element: _jsx(MaterialCreationPage, {}),
                    loader: requireAuth,
                },
                {
                    path: '/projects/:projectId/branch-analysis',
                    element: _jsx(StoryBranchAnalysisPage, {}),
                    loader: requireAuth,
                },
                {
                    path: '/projects/:projectId',
                    children: projectDetailTabRoutes,
                    element: _jsx(ProjectDetailPage, {}),
                    loader: requireAuth,
                },
                // 编辑器原型保留独立入口（首页草稿与既有入口仍指向 /projects/draft/editor）。
                { path: '/projects/:projectId/editor', element: _jsx(EditorPage, {}), loader: requireAuth },
                // 404 对匿名访问者同样可见，输错地址不需要登录提示。
                { path: '*', element: _jsx(NotFoundPage, {}) },
            ],
        },
    ];
}
